/**
 * ViewActionsCell - AG Grid cell renderer for view action buttons
 *
 * Provides quick action buttons for views in the list table:
 * - Query: Open query modal
 * - Refresh: Trigger manual refresh
 * - Materialize/Dematerialize: Toggle materialization
 * - Edit: Navigate to edit page
 * - Delete: Delete view with confirmation
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, RefreshCw, HardDrive, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ViewListItem } from '@/types/views';

interface ViewActionsCellProps {
  /** The view row being rendered. Optional to match AG Grid's `data?` shape during migration. */
  data?: ViewListItem;
  onQuery: (id: string, name: string) => void;
  onRefresh: (id: string, name: string) => void;
  onMaterialize: (id: string, name: string, type: 'virtual' | 'materialized') => void;
  onDelete: (id: string, name: string) => void;
  refreshingViews: Set<string>;
}

/**
 * ViewActionsCell Component
 *
 * Renders action buttons for a view row with loading states and confirmations.
 *
 * @example
 * ```tsx
 * // In AG Grid column definition:
 * {
 *   headerName: 'Actions',
 *   width: 180,
 *   cellRenderer: ViewActionsCell,
 *   cellRendererParams: {
 *     onQuery: handleQuery,
 *     onRefresh: handleRefresh,
 *     onMaterialize: handleMaterialize,
 *     onDelete: handleDelete,
 *     refreshingViews: refreshingSet
 *   },
 *   sortable: false,
 *   filter: false,
 *   pinned: 'right',
 * }
 * ```
 */
export const ViewActionsCell: React.FC<ViewActionsCellProps> = ({
  data,
  onQuery,
  onRefresh,
  onMaterialize,
  onDelete,
  refreshingViews,
}) => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (!data) return null;

  const isRefreshing = refreshingViews.has(data.id);
  const isBuilding = data.status === 'building';
  const isMaterialized = data.view_type === 'materialized';
  // #1575: maintained by a materialize script -- it has no builder rules, so
  // refreshing or editing it would regenerate its SQL from nothing. The backend
  // rejects both; these flags stop the UI offering actions that always fail.
  const isScriptManaged = data.is_script_managed === true;

  const handleQuery = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuery(data.id, data.name);
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isScriptManaged) return;
    if (!isRefreshing && !isBuilding) {
      onRefresh(data.id, data.name);
    }
  };

  const handleMaterialize = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMaterialize(data.id, data.name, data.view_type);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isScriptManaged) return;
    navigate(`/views/${data.id}/edit`);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteDialog(false);
    onDelete(data.id, data.name);
  };

  return (
    <>
      <div className="flex items-center gap-1">
        {/* Query Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleQuery}
          className="h-8 w-8 p-0"
          title="Query View"
          disabled={data.status === 'failed'}
        >
          <Database className="h-4 w-4" />
        </Button>

        {/* Refresh Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          className="h-8 w-8 p-0"
          title={
            isScriptManaged
              ? 'Maintained by a materialize script — refresh it by re-running that script'
              : isRefreshing
                ? 'Refresh in progress...'
                : 'Refresh View'
          }
          disabled={
            isScriptManaged || data.status === 'paused' || isBuilding || isRefreshing
          }
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin text-primary' : ''}`} />
        </Button>

        {/* Materialize/Dematerialize Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleMaterialize}
          className="h-8 w-8 p-0"
          title={isMaterialized ? 'Convert to Virtual' : 'Materialize View'}
          disabled={isBuilding || data.status === 'failed'}
        >
          <HardDrive className={`h-4 w-4 ${isMaterialized ? 'text-teal' : ''}`} />
        </Button>

        {/* Edit Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEdit}
          className="h-8 w-8 p-0"
          title={
            isScriptManaged
              ? 'Maintained by a materialize script — not editable here'
              : 'Edit View'
          }
          disabled={isScriptManaged}
        >
          <Edit className="h-4 w-4" />
        </Button>

        {/* Delete Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDeleteClick}
          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
          title="Delete View"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete View</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the view <strong>{data.name}</strong>?
              {isMaterialized && (
                <span className="block mt-2 text-destructive">
                  This will also delete the stored materialized data.
                </span>
              )}
              <span className="block mt-2">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive">
              Delete View
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
